import React, { createContext, useState, useRef, ReactNode, useMemo, useCallback } from "react";

/**
 * Values exposed by {@link PrincipalPageContext}.
 */
export interface UsePrincipalPageReturn {
  isMenuOpen: boolean;
  destinosRef: React.RefObject<null>;
  toggleMenu: () => void;
  scrollToSection: (ref: any) => void;
}

const PrincipalPageContext = createContext<UsePrincipalPageReturn | undefined>(undefined);

/**
 * Provides state for the main landing page.
 *
 * @param children - Elements that require access to the principal page context.
 */
export const PrincipalPageProvider = ({ children }: { children: ReactNode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const destinosRef = useRef(null);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const scrollToSection = useCallback((ref: any) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  }, []);


  const contextValue = useMemo(() => ({
    isMenuOpen,
    destinosRef,
    toggleMenu,
    scrollToSection,
  }), [isMenuOpen, destinosRef, toggleMenu, scrollToSection]);

  return (
    <PrincipalPageContext.Provider value={contextValue}>
      {children}
    </PrincipalPageContext.Provider>
  );
};

/**
 * Access the {@link PrincipalPageContext} values.
 *
 * @throws Error if used outside of {@link PrincipalPageProvider}.
 */
export const usePrincipalPage = () => {
  const context = React.useContext(PrincipalPageContext);
  if (context === undefined) {
    throw new Error("PrincipalPageProvider must be used within a PrincipalPageProvider");
  }
  return context;
};
